import React, { useState } from 'react';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn, inp, lbl } from '../../config/styles';
import { uid } from '../../utils/products';
import { calendarMonths, formatCalDate } from '../../utils/calendar';

// ─────────────────────────────────────────────────────────────
// CALENDAR EVENT MODAL
// ─────────────────────────────────────────────────────────────

export function CalendarEventModal({ event, onSave, onDelete, onClose }) {
  const { calCategories } = useTenantConfig();
  const [form, setForm] = useState({
    name: '', date: '', category: calCategories[0]?.id || '', supplier: '', notes: '',
    ...(event || {}),
  });
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 12, padding: 24, width: 440, boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: F.display, fontSize: 20, color: C.text }}>{event?.id ? 'Edit Event' : 'Add Event'}</div>
          <button style={btn('ghost', { fontSize: 20, padding: '2px 8px', color: C.muted })} onClick={onClose}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl()}>Event Name *</label>
            <input style={inp()} value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. Monaco Swim Launch" autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl()}>Date</label>
              <input type="date" style={inp()} value={form.date} onChange={e => upd('date', e.target.value)} />
            </div>
            <div>
              <label style={lbl()}>Category</label>
              <select style={inp()} value={form.category} onChange={e => upd('category', e.target.value)}>
                {calCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl()}>Supplier / Factory</label>
              <input style={inp()} value={form.supplier} onChange={e => upd('supplier', e.target.value)} placeholder="e.g. Cerie" />
            </div>
            <div>
              <label style={lbl()}>PO Number</label>
              <input style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }} value={form.poNumber || ''} onChange={e => upd('poNumber', e.target.value)} placeholder="e.g. PO-1303" />
            </div>
          </div>
          <div>
            <label style={lbl()}>Notes</label>
            <textarea style={{ ...inp(), minHeight: 72, resize: 'vertical' }} value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Any additional details…" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 20 }}>
          <div>
            {onDelete && <button style={btn('ghost', { color: '#dc2626', border: `1px solid #fecaca` })} onClick={onDelete}>Delete</button>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn('secondary')} onClick={onClose}>Cancel</button>
            <button style={btn('primary')} onClick={() => { if (form.name.trim()) onSave(form); }} disabled={!form.name.trim()}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CALENDAR VIEW
// ─────────────────────────────────────────────────────────────

export function CalendarView({ events, onUpdate, isManager, collections = [], onOpenCollection, onCreateCollection }) {
  const { calCategories } = useTenantConfig();
  const now = new Date();
  const defaultYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const [startYear, setStartYear] = useState(defaultYear);
  const [modal, setModal] = useState(null); // { event } or { category, monthKey } for new
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverMonth, setDragOverMonth] = useState(null);

  const months = calendarMonths(startYear);

  const findMatchingCollection = (eventName) => {
    if (!eventName || !collections.length) return null;
    const normalize = s => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ');
    const evWords = new Set(normalize(eventName).split(/\s+/).filter(Boolean));
    let best = null, bestScore = 0;
    collections.forEach((coll, idx) => {
      const collWords = normalize(coll.name || '').split(/\s+/).filter(Boolean);
      const shared = collWords.filter(w => evWords.has(w)).length;
      const score = shared / Math.max(collWords.length, 1);
      if (score > bestScore && score >= 0.75) { bestScore = score; best = idx; }
    });
    return best;
  };

  const saveEvent = (form) => {
    if (form.id) {
      onUpdate(events.map(e => e.id === form.id ? form : e));
    } else {
      const newEvent = { ...form, id: uid() };
      onUpdate([...events, newEvent]);
      if (onCreateCollection && findMatchingCollection(newEvent.name) === null) {
        const d = newEvent.date ? new Date(newEvent.date + 'T12:00:00') : new Date();
        const month = d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
        onCreateCollection(newEvent.name, month);
      }
      // Auto-navigate to the season containing the event's date
      if (newEvent.date) {
        const yr = parseInt(newEvent.date.slice(0, 4));
        const mo = parseInt(newEvent.date.slice(5, 7));
        const targetStart = mo >= 7 ? yr : yr - 1;
        if (targetStart !== startYear) setStartYear(targetStart);
      }
    }
    setModal(null);
  };

  const deleteEvent = (id) => {
    onUpdate(events.filter(e => e.id !== id));
    setModal(null);
  };

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontFamily: F.display, fontSize: 22, color: C.text }}>
          Product Calendar {startYear}–{startYear + 1}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={btn('secondary', { padding: '6px 12px' })} onClick={() => setStartYear(y => y - 1)}>← {startYear - 1}–{startYear}</button>
          <button style={btn('secondary', { padding: '6px 12px' })} onClick={() => setStartYear(y => y + 1)}>{startYear + 1}–{startYear + 2} →</button>
          {isManager && (
            <button style={btn('primary')} onClick={() => setModal({ category: calCategories[0]?.id || '', monthKey: months[0].key })}>+ Add event</button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {calCategories.map(c => (
          <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: c.fg, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 20, padding: '3px 10px', fontWeight: 500 }}>
            {c.label}
          </span>
        ))}
      </div>

      {/* Grid */}
      <div style={{ overflowX: 'auto', border: `1px solid ${C.e8}`, borderRadius: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(12, minmax(150px, 1fr))`, minWidth: 1800 }}>
          {/* Month headers */}
          {months.map(m => (
            <div key={m.key} style={{ padding: '8px 10px', background: C.fa, borderBottom: `1px solid ${C.e8}`, borderRight: `1px solid ${C.e8}`, fontSize: 11, fontWeight: 600, color: C.muted, textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {m.short}
            </div>
          ))}
          {/* Month columns — all events sorted by date */}
          {months.map(m => {
            const cellEvents = events
              .filter(e => e.date && e.date.startsWith(m.key))
              .sort((a, b) => a.date.localeCompare(b.date));
            const isOver = dragOverMonth === m.key;
            return (
              <div key={m.key}
                onDragOver={e => { e.preventDefault(); setDragOverMonth(m.key); }}
                onDragLeave={() => setDragOverMonth(null)}
                onDrop={e => {
                  e.preventDefault();
                  setDragOverMonth(null);
                  if (!draggingId) return;
                  const ev = events.find(ev => ev.id === draggingId);
                  if (!ev) return;
                  const day = ev.date?.split('-')[2] || '01';
                  onUpdate(events.map(ev2 => ev2.id === draggingId ? { ...ev2, date: `${m.key}-${day}` } : ev2));
                  setDraggingId(null);
                }}
                style={{ padding: 6, borderRight: `1px solid ${C.e8}`, minHeight: 80, display: 'flex', flexDirection: 'column', gap: 4, background: isOver ? '#f0f9ff' : 'transparent', transition: 'background 0.15s' }}>
                {cellEvents.map(e => {
                  const cat = calCategories.find(c => c.id === e.category) || calCategories[0] || {};
                  return (
                    <div
                      key={e.id}
                      draggable={isManager}
                      onDragStart={() => setDraggingId(e.id)}
                      onDragEnd={() => { setDraggingId(null); setDragOverMonth(null); }}
                      onClick={() => isManager && !draggingId && setModal({ event: e })}
                      title={e.name}
                      style={{ background: cat.bg, border: `1px solid ${cat.border}`, borderRadius: 6, padding: '5px 8px', cursor: isManager ? 'grab' : 'default', fontSize: 11, overflow: 'hidden', wordBreak: 'break-word', opacity: draggingId === e.id ? 0.4 : 1, transition: 'opacity 0.15s' }}
                    >
                      <div style={{ fontWeight: 600, color: cat.fg, lineHeight: 1.3, marginBottom: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.name}</div>
                      {e.date && <div style={{ color: cat.fg, opacity: 0.8, fontSize: 10 }}>{formatCalDate(e.date)}</div>}
                      {e.supplier && <div style={{ color: cat.fg, opacity: 0.7, fontSize: 10, fontStyle: 'italic' }}>{e.supplier}</div>}
                      {e.poNumber && <div style={{ color: cat.fg, opacity: 0.7, fontSize: 10, fontFamily: F.mono }}>{e.poNumber}</div>}
                      {(() => {
                        const idx = findMatchingCollection(e.name);
                        if (idx === null) return null;
                        return (
                          <div onClick={ev => { ev.stopPropagation(); onOpenCollection(idx); }}
                            style={{ marginTop: 4, fontSize: 10, fontWeight: 600, color: cat.fg, opacity: 0.9, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, textDecoration: 'underline' }}>
                            → Open build
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
                {isManager && (
                  <button
                    onClick={() => setModal({ category: calCategories[0]?.id || '', monthKey: m.key })}
                    style={{ background: 'none', border: `1px dashed ${C.d0}`, borderRadius: 6, padding: '3px', fontSize: 14, color: C.ph, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Add event"
                  >+</button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <CalendarEventModal
          event={modal.event || null}
          onSave={(form) => saveEvent(modal.event ? form : { ...form, date: form.date || (modal.monthKey + '-01') })}
          onDelete={modal.event ? () => deleteEvent(modal.event.id) : null}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
